import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { CA_DATA } from "../../models/data.model";
import { SharedModule } from "../../../../shared/shared.module";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { Chart } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import {
  CaParPeriodeDialogComponent,
  CaParPeriodeDialogData,
  CaPeriodScale,
} from "./ca-par-periode-dialog.component";

@Component({
  selector: "vl-widget-ca-par-periode",
  standalone: true,
  imports: [
    SharedModule,
    WidgetTitleComponent,
    WidgetPeriodFiltersComponent,
    ChipsComponent,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-ca-par-periode.component.html",
  styleUrl: "./widget-ca-par-periode.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetCaParPeriodeComponent {
  data = input<CA_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();
  elementRef = inject(ElementRef);
  private dialogsService = inject(DialogsService);

  period = signal<string>("m");
  comparison = signal<string | null>(null);
  timeScale = signal<string>("YTD");

  canvasRef = viewChild<ElementRef>("caperiode");

  chart: Chart;

  /** Libellé pluriel de l'échelle (pour tooltip du bouton "Voir tout"). */
  scaleLabelPlural = computed(() => {
    switch (this.period()) {
      case "j": return "jours";
      case "s": return "semaines";
      case "m": return "mois";
      case "a": return "années";
      default: return "périodes";
    }
  });

  /** Libellé humain du timeScale (affiché dans le header du dialog). */
  private timeScaleLabelMap: Record<string, string> = {
    "1 j": "Dernier jour",
    "5 j": "5 derniers jours",
    "1 m": "Dernier mois",
    "6 m": "6 derniers mois",
    "YTD": "Depuis le début de l'année",
    "1 a": "12 derniers mois",
    "5 a": "5 dernières années",
    "Max": "Tout l'historique",
  };

  /**
   * Ouvre le dialog détaillé "Voir tout" : même période que le widget, détail
   * par ligne avec top clients et export Excel. On applique le même
   * `filterByTimeScale` que le chart pour garder les deux vues en cohérence.
   */
  openFullList(): void {
    const raw = this.data()?.data;
    if (!raw?.length) return;

    const { data: filtered } = WidgetHelperService.filterByTimeScale(
      raw as any,
      this.timeScale(),
    );

    const dialogData: CaParPeriodeDialogData = {
      rawData: filtered as CA_DATA["data"],
      initialPeriod: this.period() as CaPeriodScale,
      timeScaleLabel: this.timeScaleLabelMap[this.timeScale()] ?? this.timeScale(),
    };

    this.dialogsService.open(
      CaParPeriodeDialogComponent,
      "min(95vw, 960px)",
      "fit-content",
      dialogData,
    );
  }

  constructor() {
    effect(
      () => {
        const data = this.data();
        const canvas = this.canvasRef();
        this.period();
        this.comparison();
        this.timeScale();

        if (data && canvas) {
          this.buildChart();
        }
      },
      { allowSignalWrites: true },
    );
  }

  buildChart() {
    if (this.data()?.data.length > 0) {
      const result = WidgetHelperService.getCaPeriodeComparisonChart(
        this.data()?.data,
        {
          timeScale: this.timeScale(),
          period: this.period(),
          comparison: this.comparison(),
        },
      );

      const bgAlt = getComputedStyle(
        this.elementRef.nativeElement,
      ).getPropertyValue("--color-on-background-alt");
      const onAccentAlt = getComputedStyle(
        this.elementRef.nativeElement,
      ).getPropertyValue("--color-on-accent-alt");

      const datasets: any[] = [
        {
          label: "CA",
          data: result.current,
          backgroundColor: onAccentAlt,
        },
      ];

      const hasComparison =
        this.comparison() && result.comparison.length > 0;

      if (hasComparison) {
        datasets.push({
          label: this.comparison() === "n-1" ? "N-1" : "Préc.",
          data: result.comparison,
          backgroundColor: onAccentAlt + "60",
        });
      }

      const compLabels = result.comparisonLabels;

      const canvas = this.canvasRef().nativeElement;
      const ctx = canvas.getContext("2d");

      if (this.chart) {
        this.chart.destroy();
      }

      this.chart = new Chart(ctx, {
        type: "bar",
        data: { datasets, labels: result.labels },
        options: {
          plugins: {
            legend: {
              display: !!hasComparison,
              labels: {
                color: bgAlt,
                boxWidth: 12,
                padding: 8,
                font: { size: 11 },
              },
            },
            tooltip: {
              callbacks: {
                title: (items) => {
                  if (!items.length) return "";
                  const idx = items[0].dataIndex;
                  const currentLabel = result.labels[idx] ?? "";
                  if (hasComparison && compLabels[idx]) {
                    return `${currentLabel} vs ${compLabels[idx]}`;
                  }
                  return currentLabel;
                },
                label: (context) => {
                  const value = context.parsed.y ?? 0;
                  const datasetIdx = context.datasetIndex;
                  const idx = context.dataIndex;
                  let periodLabel = "";
                  if (datasetIdx === 1 && compLabels[idx]) {
                    periodLabel = ` (${compLabels[idx]})`;
                  }
                  return `${context.dataset.label}${periodLabel}: ${WidgetHelperService.formatNumeric(value)}€`;
                },
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                color: bgAlt,
                maxRotation: 45,
                font: { size: 10 },
              },
            },
            y: {
              ticks: {
                color: bgAlt,
                font: { size: 10 },
                callback: (value) =>
                  WidgetHelperService.formatNumeric(+value) + "€",
              },
            },
          },
        },
      });
    }
  }
}
